/**
 * FICTIONAL DEMO DATA — for running the frontend without a backend. Not real people, projects or figures.
 *
 * Generated deterministically relative to an anchor date (default: today) so the demo always shows
 * recent activity: ~4 weeks of work logs and Project Logs, deadlines ahead and behind, deadline changes,
 * one metric with no type set, and one text-only metric value.
 */
import type { Backup, DeadlineExtension, Member, MetricKind, Project, ProjectMetric, ProjectUpdate, WorkLog } from '@/types/models';
import { addDays, today } from '@/domain/format';

/** Small deterministic PRNG (mulberry32) so the demo is identical on every run. */
function prng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const MEMBERS: [string, string, string][] = [
  ['m01', 'Demo Manager', 'Management'],
  ['m02', 'Demo Coordinator', 'Project Manager'],
  ['m03', 'Demo Developer A', 'Developer'],
  ['m04', 'Demo Developer B', 'Developer'],
  ['m05', 'Demo Developer C', 'Developer'],
  ['m06', 'Demo Designer', 'Design'],
  ['m07', 'Demo Analyst A', 'Content / QA'],
  ['m08', 'Demo Analyst B', 'Content / QA'],
  ['m09', 'Demo Writer', 'Content'],
  ['m10', 'Demo Support A', 'Support Team'],
  ['m11', 'Demo Support B', 'Support Team'],
  ['m12', 'Demo Tester', 'QA'],
];

interface ProjectSpec {
  id: string;
  name: string;
  service: string;
  lead: string;
  status: Project['status'];
  planned: number | null; // days from anchor
  extensions?: [on: number, from: number, to: number, reason: string][];
  members: string[];
  metrics: [string, MetricKind][];
  completed: string;
  pending: string;
  delayReason?: string;
  links?: boolean;
  /** Project Log every n weekdays; 0 = none */
  logEvery: number;
  /** last Project Log n days before the anchor (to show stale projects) */
  lastLogBefore?: number;
}

const PROJECTS: ProjectSpec[] = [
  { id: 'p01', name: 'Sample Project Alpha', service: 'Example service: customer onboarding portal', lead: 'Demo Developer A', status: 'Live', planned: 40, members: ['m03', 'm04', 'm07', 'm10'], metrics: [['Total Users', 'todate'], ['New Users', 'daily'], ['Active Users', 'asof'], ['Completion rate', 'asof']], completed: 'Example: onboarding flow is live', pending: 'Example: reporting improvements', links: true, logEvery: 1 },
  { id: 'p02', name: 'Sample Project Beta', service: 'Example service: task tracking', lead: 'Demo Developer B', status: 'In Progress', planned: -20, extensions: [[-22, -20, -6, 'Example reason: two sample modules were added to the scope'], [-8, -6, 4, 'Example reason: the demo reviewer was unavailable for a week']], members: ['m04', 'm05', 'm12'], metrics: [['Tasks completed', 'daily'], ['Open tasks', 'asof'], ['Overdue items', 'asof']], completed: 'Example: task board and reminders', pending: 'Example: exports and permissions', logEvery: 2 },
  { id: 'p03', name: 'Sample Project Gamma', service: 'Example service: learning modules', lead: 'Demo Analyst A', status: 'Planning', planned: 75, members: ['m07', 'm08', 'm09'], metrics: [['Modules drafted', 'todate']], completed: 'Example: outline agreed', pending: 'Example: first three modules', logEvery: 3, lastLogBefore: 5 },
  { id: 'p04', name: 'Sample Project Delta', service: 'Example service: billing integration', lead: 'Demo Developer C', status: 'Delayed', planned: -6, members: ['m05', 'm06'], metrics: [], completed: 'Example: invoice screens', pending: 'Example: payment provider connection', delayReason: 'Example reason: waiting for sample test credentials', logEvery: 2, lastLogBefore: 3 },
  { id: 'p05', name: 'Sample Project Epsilon', service: 'Example service: partner portal', lead: '', status: 'On Hold', planned: null, members: [], metrics: [], completed: '', pending: 'Example: restart after partner sign-off', delayReason: 'Example reason: waiting on a sample external team', logEvery: 0 },
  { id: 'p06', name: 'Sample Project Zeta', service: 'Example service: internal survey', lead: 'Demo Coordinator', status: 'Completed', planned: -30, members: ['m09', 'm11'], metrics: [], completed: 'Example: survey delivered and closed', pending: '', logEvery: 0 },
  { id: 'p07', name: 'Sample Project Eta', service: 'Example service: mobile app', lead: '', status: 'In Progress', planned: 8, extensions: [[-12, 8, 18, 'Example reason: extra sample screens were requested']], members: ['m03', 'm06', 'm12'], metrics: [['Screens done', 'todate'], ['Open bugs', 'asof']], completed: 'Example: sign-in and home screens', pending: 'Example: settings and offline mode', logEvery: 2 },
  { id: 'p08', name: 'Sample Project Theta', service: 'Example service: support desk', lead: 'Demo Support A', status: 'Live', planned: null, members: ['m10', 'm11'], metrics: [['Tickets closed', 'daily'], ['Backlog', 'asof']], completed: 'Example: ticket queue live', pending: 'Example: satisfaction survey', links: true, logEvery: 1 },
];

/** member → [project ids] they log time on ('' = General) */
const WORK: Record<string, string[]> = {
  m01: [''],
  m02: [''],
  m03: ['p01', 'p07'],
  m04: ['p01', 'p02'],
  m05: ['p02', 'p04'],
  m06: ['p04', 'p07'],
  m07: ['p01', 'p03'],
  m08: ['p03'],
  m09: ['p03'],
  m10: ['p01', 'p08'],
  m11: ['p08'],
  m12: ['p02', 'p07'],
};
const TASKS = ['Example task: bug fixes', 'Example task: feature work', 'Example task: code review', 'Example task: testing', 'Example task: documentation', 'Example task: design updates', 'Example task: content review', 'Example task: support tickets'];
const NOTES = ['Example update: progress on the sample milestones.', 'Example update: demo feedback reviewed and next steps agreed.', 'Example update: no blockers reported today.', 'Example update: sample release prepared for review.'];

export function buildDemoData(anchor: string = today()): Backup {
  const rnd = prng(20260930);
  const pick = <T,>(a: T[]) => a[Math.floor(rnd() * a.length)];
  const at = (n: number) => addDays(anchor, n);
  const members: Member[] = MEMBERS.map(([id, name, role]) => ({ id, name, role }));
  const projects: Project[] = PROJECTS.map((s) => {
    const ext: DeadlineExtension[] = (s.extensions || []).map(([on, from, to, reason]) => ({ on: at(on), from: at(from), to: at(to), reason }));
    return {
      id: s.id,
      name: s.name,
      service: s.service,
      lead: s.lead,
      status: s.status,
      planned: s.planned === null ? '' : at(s.planned),
      revised: ext.length ? ext[ext.length - 1].to : '',
      deadlineBasis: s.planned === null ? undefined : 'Example basis: agreed with the sample client',
      delayReason: ext.length ? ext[ext.length - 1].reason : s.delayReason || '',
      completed: s.completed,
      pending: s.pending,
      extensions: ext,
      links: s.links ? [{ label: 'Website', url: 'https://example.com' }] : [],
      members: [...s.members],
      metricKeys: s.metrics.map(([l]) => l),
      metricKinds: Object.fromEntries(s.metrics),
    };
  });

  const weekday = (iso: string) => {
    const d = new Date(iso + 'T00:00:00').getDay();
    return d !== 0 && d !== 6;
  };
  const logs: WorkLog[] = [];
  let li = 0;
  for (let n = -27; n <= 0; n++) {
    const date = at(n);
    if (!weekday(date)) continue;
    for (const [mid, projs] of Object.entries(WORK)) {
      if (n === 0 && !['m02', 'm03', 'm10'].includes(mid)) continue; // today: only a few people have logged so far
      if (rnd() < 0.1) continue; // day off
      if (projs[0] === '') {
        const h = mid === 'm01' ? pick([0.5, 1, 1.5, 2]) : pick([4, 5, 6, 7]);
        logs.push({ id: `wl-${String(++li).padStart(4, '0')}`, date, projectId: '', memberId: mid, hours: h, work: mid === 'm01' ? 'Example: project oversight' : 'Example: coordination across sample projects' });
        continue;
      }
      const split = projs.length === 1 ? [8] : pick([[4, 4], [6, 2], [5, 3], [8, 0]]);
      projs.forEach((pid, i) => {
        const h = split[i] ?? 0;
        if (!h) return;
        logs.push({ id: `wl-${String(++li).padStart(4, '0')}`, date, projectId: pid, memberId: mid, hours: h, work: pick(TASKS) });
      });
    }
  }

  const updates: ProjectUpdate[] = [];
  let ui = 0;
  const running: Record<string, number> = { 'Total Users': 1180, 'Modules drafted': 2, 'Screens done': 6 };
  const level: Record<string, number> = { 'Active Users': 310, 'Open tasks': 128, 'Overdue items': 34, 'Open bugs': 41, Backlog: 86, 'Completion rate': 58 };
  for (const s of PROJECTS) {
    if (!s.logEvery) continue;
    let k = 0;
    for (let n = -27; n <= -1; n++) {
      const date = at(n);
      if (!weekday(date)) continue;
      if (s.lastLogBefore && n > -s.lastLogBefore) continue;
      if (k++ % s.logEvery !== 0) continue;
      const metrics: ProjectMetric[] = s.metrics.map(([label, kind]) => {
        if (kind === 'daily') {
          const v = Math.floor(rnd() * (label === 'New Users' ? 16 : 22)) + (label === 'Tickets closed' ? 8 : 2);
          if (label === 'New Users') running['Total Users'] += v;
          return { label, value: String(v) };
        }
        if (kind === 'todate') {
          if (label !== 'Total Users') running[label] += Math.floor(rnd() * 2);
          return { label, value: running[label].toLocaleString('en-IN') };
        }
        level[label] += label === 'Completion rate' ? Math.floor(rnd() * 3) : Math.floor(rnd() * 7) - 4;
        return { label, value: label === 'Completion rate' ? `${level[label]}%` : String(level[label]) };
      });
      if (s.id === 'p08') {
        metrics.push({ label: 'Region A — Items processed', value: String(100 + Math.floor(rnd() * 90)) }); // no type set on purpose
        metrics.push({ label: 'Average handling time', value: `${10 + Math.floor(rnd() * 6)}m ${Math.floor(rnd() * 59)}s` }); // text-only value
      }
      updates.push({ id: `pu-${String(++ui).padStart(4, '0')}`, date, projectId: s.id, note: pick(NOTES), metrics });
    }
  }
  if (updates.length) updates.push({ id: `pu-${String(++ui).padStart(4, '0')}`, date: at(-26), projectId: 'p05', note: 'Example update: paused until the sample partner confirms.', metrics: [] });

  return { projects, members, logs, updates, settings: { reportTitle: 'Daily Update - {date} - Sample Projects', generalMembers: ['m01', 'm02'] } };
}

export const emptyData = (): Backup => ({ projects: [], members: [], logs: [], updates: [], settings: { reportTitle: 'Daily Update - {date}', generalMembers: [] } });
